import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// MACFilter now receives macs from parent (PositionIDManagement)
// so there is no internal fetch — avoids duplicate API call
const MACFilter = ({ macs = [], loading = false, selectedMAC, selectedSOW, onSelectMAC, onSelectSOW, singleMacMode = false }) => {
  const theme = useTheme();
  const t = theme.custom;

  const totalCount = macs.reduce((sum, m) => sum + (m.sowCount || 0), 0);
  const singleSows = singleMacMode ? (macs[0]?.sows ?? []) : [];

  return (
    <Box
      sx={{
        width: "200px",
        flexShrink: 0,
        borderRight: `1px solid ${t.border2}`,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box sx={{ padding: "14px 16px 10px" }}>
        <Typography
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: t.text3,
          }}
        >
          {singleMacMode ? "SOW" : "MAC / SOW"}
        </Typography>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
          <CircularProgress size={16} sx={{ color: t.accent }} />
        </Box>
      )}

      {/* Single-MAC mode: show All + SOW list directly, no MAC row */}
      {!loading && singleMacMode && (
        <>
          <MACRow
            label="All"
            count={singleSows.length}
            active={selectedSOW === null}
            onClick={() => onSelectSOW(null)}
            t={t}
          />
          {singleSows.map((sow) => (
            <Box
              key={sow.sowId}
              onClick={() => onSelectSOW(sow.sowId)}
              sx={{
                padding: "5px 16px",
                fontFamily: "'Inter', sans-serif",
                fontSize: "11px",
                cursor: "pointer",
                borderLeft: selectedSOW === sow.sowId
                  ? `2px solid ${t.accent}`
                  : "2px solid transparent",
                color: selectedSOW === sow.sowId ? t.text : t.text3,
                background: selectedSOW === sow.sowId ? t.accent2 : "transparent",
                transition: "all 0.15s",
                "&:hover": { color: t.text2, background: t.surface2 },
              }}
            >
              {sow.sowName}
            </Box>
          ))}
        </>
      )}

      {/* Normal multi-MAC mode */}
      {!loading && !singleMacMode && (
        <>
          <MACRow
            label="All MACs"
            count={totalCount}
            active={selectedMAC === null}
            onClick={() => {
              onSelectMAC(null);
              onSelectSOW(null);
            }}
            t={t}
          />

          {macs.map((mac) => (
            <Box key={mac.macId}>
              <MACRow
                label={mac.macName}
                count={mac.sowCount}
                active={selectedMAC === mac.macId}
                onClick={() => {
                  onSelectMAC(mac.macId);
                  onSelectSOW(null);
                }}
                t={t}
              />

              {selectedMAC === mac.macId &&
                mac.sows?.map((sow) => (
                  <Box
                    key={sow.sowId}
                    onClick={() => onSelectSOW(sow.sowId)}
                    sx={{
                      padding: "5px 16px 5px 28px",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      cursor: "pointer",
                      borderLeft: selectedSOW === sow.sowId
                        ? `2px solid ${t.accent}`
                        : "2px solid transparent",
                      color: selectedSOW === sow.sowId ? t.text : t.text3,
                      background: selectedSOW === sow.sowId ? t.accent2 : "transparent",
                      transition: "all 0.15s",
                      "&:hover": { color: t.text2, background: t.surface2 },
                    }}
                  >
                    {sow.sowName}
                  </Box>
                ))}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};

const MACRow = ({ label, count, active, onClick, t }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 16px",
      cursor: "pointer",
      borderLeft: active ? `2px solid ${t.accent}` : "2px solid transparent",
      background: active ? t.accent2 : "transparent",
      transition: "all 0.15s",
      "&:hover": { background: active ? t.accent2 : t.surface2 },
    }}
  >
    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "13px",
        fontWeight: active ? 600 : 400,
        color: active ? t.text : t.text2,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px",
        color: t.text3,
      }}
    >
      {count}
    </Typography>
  </Box>
);

export default MACFilter;